import { useEffect, useState } from "react";
import { GridMetaData, createUrlSearchParams } from "models/grids";
import { ITask, TaskFilter } from "models/tasks";
import { useCurrentUser, useCentralApi } from "hooks/shared";

type GetTasksResult = {
    tasks: ITask[],
    totalItems: number,
    hasViewProblemLogPrivileges: boolean
};

type TasksResult = [
    ITask[],
    number,
    boolean,
    string | null
];

const useTaskDependencies: (taskId: number | undefined, dependecyType: string, gridMetadata: GridMetaData, preferencesLoaded: boolean) => TasksResult = (taskId, dependecyType, gridMetadata, preferencesLoaded) => {
    const [ _lastQuery, _setLastQuery ] = useState<string>('');
    const [ _lastDependencyType, _setLastDependencyType ] = useState<string>('');
    const [ result, setResult ] = useState<ITask[]>([]);
    const [ hasViewProblemLogPrivileges, setHasViewProblemLogPrivileges ] = useState<boolean>(false);
    const [ total, setTotal ] = useState<number>(0);
    const [ error, setError ] = useState<string | null>(null);
    const [ loading, setLoading ] = useState<boolean>(true);    
    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    useEffect(() => {        
        const controller = new AbortController();
        const fetchTasks = async () => {
            const searchParams = createUrlSearchParams(gridMetadata);
            const query = `?${searchParams}`;

            if ((_lastQuery === query) && _lastQuery === dependecyType) return;

            if (user && user.id_token && preferencesLoaded) {
                setLoading(true);
                try {
                    const resp = await fetcher(`/api/v3/tasks/${taskId}/${dependecyType}/taskdependencies${query}`, {
                        signal: controller.signal
                    });

                    if (!resp.ok) {
                        // RESPOND TO ERRORS
                    }

                    const data = await resp.json() as GetTasksResult;
                    setResult(data.tasks);
                    setHasViewProblemLogPrivileges(data.hasViewProblemLogPrivileges);

                    if (data.totalItems !== total) {
                        setTotal(data.totalItems);
                    }

                    _setLastQuery(query);
                    _setLastDependencyType(dependecyType);
                } catch (err) {
                    // Will be done in BFLY-8658 or BFLY-9022
                } finally {
                    if (!controller.signal.aborted)
                        setLoading(false);
                }
            }
        };
        
        fetchTasks();
        return () => controller.abort();
    }, [ gridMetadata, user, preferencesLoaded, dependecyType ]);

    return [
        result,
        total,
        loading,
        error
    ];
};

export default useTaskDependencies;